import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  AthleteView,
  DepartmentView,
  PageResponse,
  RosterEntryView,
  SeasonView,
  SupportedLocale,
  TeamView,
  TrainingGroupView,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { athleteDisplayName, formatCalendarDate } from './athletes';
import { RemoteSelect, type RemoteOption } from './RemoteSelect';
import { RolloverSection } from './RolloverSection';
import {
  canManageDepartment,
  canSubmitSeason,
  canSubmitTeam,
  categoryLabel,
  chooseSeason,
  emptySeasonForm,
  emptyTeamForm,
  isValidShirtEntry,
  seasonRequestBody,
  seasonStatusKeys,
  shirtNumberFrom,
  structurePath,
  teamRequestBody,
  type SeasonForm,
  type TeamForm,
} from './teams';
import { TaskSection } from './TaskSection';

export interface TeamsPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
  /** Whether the person may look up athletes club-wide to place them; the server enforces it too. */
  canPlaceAthletes: boolean;
}

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

interface RosterListProps {
  locale: SupportedLocale;
  base: string;
  csrfToken: string;
  collection: 'teams' | 'training-groups';
  containerId: string;
  canManage: boolean;
  canPlace: boolean;
}

/** The members of one team or training group, with ending and adding for those who may. */
function RosterList({
  locale,
  base,
  csrfToken,
  collection,
  containerId,
  canManage,
  canPlace,
}: RosterListProps): React.JSX.Element {
  const isTeam = collection === 'teams';
  const rosterPath = `${base}/${collection}/${containerId}/members`;
  const [entries, setEntries] = useState<RosterEntryView[] | null>(null);
  const [includeEnded, setIncludeEnded] = useState(false);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmingEnd, setConfirmingEnd] = useState<string | null>(null);
  const [athleteId, setAthleteId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [shirtText, setShirtText] = useState('');
  // Changing the key empties the picker after a placement was stored.
  const [formKey, setFormKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const loaded = await apiRequest<RosterEntryView[]>(
        `${rosterPath}${includeEnded ? '?includeEnded=true' : ''}`,
      );
      setEntries(loaded);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [rosterPath, includeEnded]);

  useEffect(() => {
    void load();
  }, [load]);

  const findAthletes = useCallback(
    async (text: string): Promise<RemoteOption[]> => {
      const page = await apiRequest<PageResponse<AthleteView>>(
        `${base}/athletes?limit=20&q=${encodeURIComponent(text)}`,
      );
      return page.items.map((athlete) => ({
        id: athlete.id,
        label: `${athleteDisplayName(athlete, locale)} (${formatCalendarDate(athlete.dateOfBirth, locale)})`,
      }));
    },
    [base, locale],
  );

  async function endPlacement(entry: RosterEntryView): Promise<void> {
    setErrorKey(null);
    setSaved(false);
    setConfirmingEnd(null);
    try {
      await apiRequest<RosterEntryView>(`${rosterPath}/${entry.id}/end`, {
        method: 'POST',
        body: {},
        csrfToken,
        ifMatchVersion: entry.version,
      });
      setSaved(true);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
    // Reload either way: after a conflict the roster must show the placement's current state.
    await load();
  }

  async function addMember(): Promise<void> {
    setErrorKey(null);
    setSaved(false);
    try {
      await apiRequest<RosterEntryView>(rosterPath, {
        method: 'POST',
        body: {
          athleteId,
          ...(fromDate === '' ? {} : { fromDate }),
          ...(isTeam ? { shirtNumber: shirtNumberFrom(shirtText) } : {}),
        },
        csrfToken,
      });
      setSaved(true);
      setAthleteId('');
      setFromDate('');
      setShirtText('');
      setFormKey((current) => current + 1);
      await load();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  return (
    <Box sx={{ mt: 1, ml: { sm: 2 } }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={includeEnded}
            onChange={(event) => {
              setIncludeEnded(event.target.checked);
            }}
          />
        }
        label={translate(locale, 'teams.roster.includeEnded')}
      />
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {saved && errorKey === null && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {translate(locale, 'teams.saved')}
        </Alert>
      )}
      {entries === null && errorKey === null && (
        <Typography>{translate(locale, 'teams.loading')}</Typography>
      )}
      {entries?.length === 0 && <Typography>{translate(locale, 'teams.roster.empty')}</Typography>}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {entries?.map((entry) => (
          <Box
            component="li"
            key={entry.id}
            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}
          >
            <Typography sx={{ fontWeight: 500 }}>{athleteDisplayName(entry, locale)}</Typography>
            <Typography color="text.secondary" variant="body2">
              {translate(locale, 'teams.roster.from')} {formatCalendarDate(entry.fromDate, locale)}
              {' · '}
              {translate(locale, 'teams.roster.until')}{' '}
              {entry.untilDate === null
                ? translate(locale, 'teams.roster.openEnded')
                : formatCalendarDate(entry.untilDate, locale)}
              {isTeam &&
                entry.shirtNumber !== null &&
                ` · ${translate(locale, 'teams.roster.shirt')} ${String(entry.shirtNumber)}`}
            </Typography>
            {canManage && entry.untilDate === null && (
              <Button
                size="small"
                color={confirmingEnd === entry.id ? 'error' : 'primary'}
                variant={confirmingEnd === entry.id ? 'contained' : 'outlined'}
                onClick={() => {
                  if (confirmingEnd === entry.id) void endPlacement(entry);
                  else setConfirmingEnd(entry.id);
                }}
              >
                {translate(
                  locale,
                  confirmingEnd === entry.id ? 'teams.roster.end.confirm' : 'teams.roster.end',
                )}
              </Button>
            )}
          </Box>
        ))}
      </Stack>
      {canManage && canPlace && (
        <Box
          component="form"
          key={formKey}
          sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}
          onSubmit={(event) => {
            event.preventDefault();
            void addMember();
          }}
        >
          <RemoteSelect
            label={translate(locale, 'teams.roster.add.athlete')}
            noMatchesText={translate(locale, 'teams.roster.add.noMatches')}
            load={findAthletes}
            onSelect={setAthleteId}
          />
          <TextField
            size="small"
            type="date"
            label={translate(locale, 'teams.roster.from')}
            value={fromDate}
            onChange={(event) => {
              setFromDate(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {isTeam && (
            <TextField
              size="small"
              label={translate(locale, 'teams.roster.shirt')}
              value={shirtText}
              error={!isValidShirtEntry(shirtText)}
              onChange={(event) => {
                setShirtText(event.target.value);
              }}
              slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 2 } }}
              sx={{ width: 130 }}
            />
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={athleteId === '' || !isValidShirtEntry(shirtText)}
          >
            {translate(locale, 'teams.roster.add.submit')}
          </Button>
        </Box>
      )}
    </Box>
  );
}

interface StructureRowProps extends Omit<RosterListProps, 'containerId' | 'collection'> {
  collection: 'teams' | 'training-groups';
  id: string;
  title: string;
  detail: string | null;
}

/** One team or group with a toggle that loads its roster only when it is opened. */
function StructureRow({ id, title, detail, ...roster }: StructureRowProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <Box component="li" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, padding: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        {detail !== null && <Chip size="small" label={detail} />}
        <Button
          size="small"
          sx={{ ml: 'auto' }}
          aria-expanded={open}
          onClick={() => {
            setOpen((current) => !current);
          }}
        >
          {translate(roster.locale, open ? 'teams.roster.hide' : 'teams.roster.show')}
        </Button>
      </Box>
      {open && <RosterList {...roster} containerId={id} />}
    </Box>
  );
}

export function TeamsPanel({
  locale,
  organizationId,
  csrfToken,
  canPlaceAthletes,
}: TeamsPanelProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}`;
  const [departments, setDepartments] = useState<DepartmentView[] | null>(null);
  const [seasons, setSeasons] = useState<SeasonView[] | null>(null);
  const [seasonId, setSeasonId] = useState<string | null>(null);
  const [teams, setTeams] = useState<TeamView[] | null>(null);
  const [groups, setGroups] = useState<TrainingGroupView[] | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [seasonForm, setSeasonForm] = useState<SeasonForm | null>(null);
  const [teamForm, setTeamForm] = useState<TeamForm | null>(null);
  const [groupName, setGroupName] = useState('');

  const loadSeasons = useCallback(async () => {
    try {
      const [loadedDepartments, loadedSeasons] = await Promise.all([
        apiRequest<DepartmentView[]>(`${base}/departments`),
        apiRequest<SeasonView[]>(structurePath(base, 'seasons')),
      ]);
      setDepartments(loadedDepartments);
      setSeasons(loadedSeasons);
      setSeasonId((current) => chooseSeason(loadedSeasons, current));
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base]);

  const loadSeasonContent = useCallback(
    async (selected: string) => {
      try {
        const [loadedTeams, loadedGroups] = await Promise.all([
          apiRequest<TeamView[]>(structurePath(base, 'teams', selected)),
          apiRequest<TrainingGroupView[]>(structurePath(base, 'training-groups', selected)),
        ]);
        setTeams(loadedTeams);
        setGroups(loadedGroups);
      } catch (error) {
        setErrorKey(errorKeyOf(error));
      }
    },
    [base],
  );

  useEffect(() => {
    void loadSeasons();
  }, [loadSeasons]);

  useEffect(() => {
    setTeams(null);
    setGroups(null);
    setConfirmingClose(false);
    if (seasonId !== null) void loadSeasonContent(seasonId);
  }, [seasonId, loadSeasonContent]);

  const season = seasons?.find((candidate) => candidate.id === seasonId);
  const department = departments?.find((candidate) => candidate.id === season?.departmentId);
  const canManageSeason =
    season !== undefined &&
    departments !== null &&
    canManageDepartment(departments, season.departmentId);
  const canAddToSeason = canManageSeason && season.status !== 'closed';
  const anyManageable = departments?.some((candidate) => candidate.canManage) === true;

  /** Runs a change and reports the outcome; the lists are reloaded even after a failure. */
  async function change(work: () => Promise<void>): Promise<boolean> {
    setErrorKey(null);
    setSaved(false);
    setConfirmingClose(false);
    try {
      await work();
      setSaved(true);
      return true;
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      return false;
    }
  }

  async function transition(target: SeasonView, action: 'activate' | 'close'): Promise<void> {
    await change(async () => {
      await apiRequest<SeasonView>(`${base}/seasons/${target.id}/${action}`, {
        method: 'POST',
        body: {},
        csrfToken,
        ifMatchVersion: target.version,
      });
    });
    await loadSeasons();
  }

  async function createSeason(form: SeasonForm): Promise<void> {
    let createdId: string | null = null;
    const succeeded = await change(async () => {
      const created = await apiRequest<SeasonView>(`${base}/seasons`, {
        method: 'POST',
        body: seasonRequestBody(form),
        csrfToken,
      });
      createdId = created.id;
    });
    if (!succeeded) return;
    setSeasonForm(null);
    await loadSeasons();
    if (createdId !== null) setSeasonId(createdId);
  }

  async function createTeam(selected: SeasonView, form: TeamForm): Promise<void> {
    const succeeded = await change(async () => {
      await apiRequest<TeamView>(`${base}/teams`, {
        method: 'POST',
        body: teamRequestBody(selected.id, form),
        csrfToken,
      });
    });
    if (!succeeded) return;
    setTeamForm(null);
    await loadSeasonContent(selected.id);
  }

  async function createGroup(selected: SeasonView): Promise<void> {
    const succeeded = await change(async () => {
      await apiRequest<TrainingGroupView>(`${base}/training-groups`, {
        method: 'POST',
        body: { seasonId: selected.id, name: groupName },
        csrfToken,
      });
    });
    if (!succeeded) return;
    setGroupName('');
    await loadSeasonContent(selected.id);
  }

  const rosterProps = {
    locale,
    base,
    csrfToken,
    canManage: canAddToSeason,
    canPlace: canPlaceAthletes,
  };

  return (
    <Box component="section" aria-labelledby="teams-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="teams-title" gutterBottom>
        {translate(locale, 'nav.teams')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {saved && errorKey === null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'teams.saved')}
        </Alert>
      )}
      {seasons === null && errorKey === null && (
        <Typography>{translate(locale, 'teams.loading')}</Typography>
      )}
      {departments?.length === 0 && (
        <Typography>{translate(locale, 'teams.noDepartments')}</Typography>
      )}
      {seasons?.length === 0 && <Typography>{translate(locale, 'teams.season.none')}</Typography>}
      {seasons !== null && seasons.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <TextField
            select
            size="small"
            label={translate(locale, 'teams.season.select')}
            value={seasonId ?? ''}
            onChange={(event) => {
              setSeasonId(event.target.value);
            }}
            sx={{ minWidth: 240 }}
          >
            {seasons.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {season !== undefined && (
            <>
              <Chip label={translate(locale, seasonStatusKeys[season.status])} />
              <Typography color="text.secondary" variant="body2">
                {formatCalendarDate(season.startsOn, locale)} –{' '}
                {formatCalendarDate(season.endsOn, locale)}
                {department !== undefined && ` · ${department.name}`}
              </Typography>
              {canManageSeason && season.status === 'draft' && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    void transition(season, 'activate');
                  }}
                >
                  {translate(locale, 'teams.season.activate')}
                </Button>
              )}
              {canManageSeason && season.status === 'active' && (
                <Button
                  variant={confirmingClose ? 'contained' : 'outlined'}
                  color={confirmingClose ? 'error' : 'primary'}
                  onClick={() => {
                    if (confirmingClose) void transition(season, 'close');
                    else setConfirmingClose(true);
                  }}
                >
                  {translate(
                    locale,
                    confirmingClose ? 'teams.season.close.confirm' : 'teams.season.close',
                  )}
                </Button>
              )}
            </>
          )}
        </Box>
      )}

      {season !== undefined && (
        <>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
            {translate(locale, 'teams.teams.title')}
          </Typography>
          {teams?.length === 0 && <Typography>{translate(locale, 'teams.teams.empty')}</Typography>}
          <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: '8px 0' }}>
            {teams?.map((team) => (
              <StructureRow
                key={team.id}
                {...rosterProps}
                collection="teams"
                id={team.id}
                title={team.name}
                detail={categoryLabel(locale, team.categoryCode)}
              />
            ))}
          </Stack>
          {canAddToSeason && department !== undefined && (
            <Box sx={{ mb: 3 }}>
              {teamForm === null ? (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setTeamForm(emptyTeamForm(department.categoryCodes));
                  }}
                >
                  {translate(locale, 'teams.team.create.title')}
                </Button>
              ) : (
                <Box
                  component="form"
                  sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    void createTeam(season, teamForm);
                  }}
                >
                  <TextField
                    required
                    size="small"
                    label={translate(locale, 'teams.team.name')}
                    value={teamForm.name}
                    onChange={(event) => {
                      setTeamForm({ ...teamForm, name: event.target.value });
                    }}
                    slotProps={{ htmlInput: { maxLength: 200 } }}
                  />
                  <TextField
                    select
                    size="small"
                    label={translate(locale, 'teams.team.category')}
                    value={teamForm.categoryCode}
                    onChange={(event) => {
                      setTeamForm({ ...teamForm, categoryCode: event.target.value });
                    }}
                    sx={{ minWidth: 140 }}
                  >
                    {department.categoryCodes.map((code) => (
                      <MenuItem key={code} value={code}>
                        {categoryLabel(locale, code)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    label={translate(locale, 'teams.team.division')}
                    value={teamForm.competitionDivision}
                    onChange={(event) => {
                      setTeamForm({ ...teamForm, competitionDivision: event.target.value });
                    }}
                    slotProps={{ htmlInput: { maxLength: 200 } }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label={translate(locale, 'teams.team.ordinal')}
                    value={teamForm.teamOrdinal}
                    onChange={(event) => {
                      setTeamForm({ ...teamForm, teamOrdinal: event.target.value });
                    }}
                    slotProps={{ htmlInput: { min: 1, max: 20 } }}
                    sx={{ width: 120 }}
                  />
                  <Button type="submit" variant="contained" disabled={!canSubmitTeam(teamForm)}>
                    {translate(locale, 'teams.team.create.submit')}
                  </Button>
                </Box>
              )}
            </Box>
          )}

          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
            {translate(locale, 'teams.groups.title')}
          </Typography>
          {groups?.length === 0 && (
            <Typography>{translate(locale, 'teams.groups.empty')}</Typography>
          )}
          <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: '8px 0' }}>
            {groups?.map((group) => (
              <StructureRow
                key={group.id}
                {...rosterProps}
                collection="training-groups"
                id={group.id}
                title={group.name}
                detail={null}
              />
            ))}
          </Stack>
          {canAddToSeason && (
            <Box
              component="form"
              sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}
              onSubmit={(event) => {
                event.preventDefault();
                void createGroup(season);
              }}
            >
              <TextField
                size="small"
                label={translate(locale, 'teams.group.name')}
                value={groupName}
                onChange={(event) => {
                  setGroupName(event.target.value);
                }}
                slotProps={{ htmlInput: { maxLength: 200 } }}
              />
              <Button type="submit" variant="outlined" disabled={groupName.trim() === ''}>
                {translate(locale, 'teams.group.create.submit')}
              </Button>
            </Box>
          )}
          {canManageSeason && department !== undefined && (
            <TaskSection locale={locale} titleKey="teams.rollover.title">
              <RolloverSection
                key={season.id}
                locale={locale}
                base={base}
                csrfToken={csrfToken}
                season={season}
                department={department}
                onRolledOver={async (newSeasonId) => {
                  await loadSeasons();
                  setSeasonId(newSeasonId);
                  setSaved(true);
                }}
              />
            </TaskSection>
          )}
        </>
      )}

      {anyManageable && departments !== null && (
        <Box sx={{ mt: 2 }}>
          {seasonForm === null ? (
            <Button
              variant="outlined"
              onClick={() => {
                setSeasonForm(emptySeasonForm(departments));
              }}
            >
              {translate(locale, 'teams.season.create.title')}
            </Button>
          ) : (
            <Box
              component="form"
              sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}
              onSubmit={(event) => {
                event.preventDefault();
                void createSeason(seasonForm);
              }}
            >
              <TextField
                select
                size="small"
                label={translate(locale, 'teams.department')}
                value={seasonForm.departmentId}
                onChange={(event) => {
                  setSeasonForm({ ...seasonForm, departmentId: event.target.value });
                }}
                sx={{ minWidth: 180 }}
              >
                {departments
                  .filter((option) => option.canManage)
                  .map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
              </TextField>
              <TextField
                required
                size="small"
                label={translate(locale, 'teams.season.name')}
                value={seasonForm.label}
                onChange={(event) => {
                  setSeasonForm({ ...seasonForm, label: event.target.value });
                }}
                slotProps={{ htmlInput: { maxLength: 100 } }}
              />
              <TextField
                required
                size="small"
                type="date"
                label={translate(locale, 'teams.season.startsOn')}
                value={seasonForm.startsOn}
                onChange={(event) => {
                  setSeasonForm({ ...seasonForm, startsOn: event.target.value });
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                required
                size="small"
                type="date"
                label={translate(locale, 'teams.season.endsOn')}
                value={seasonForm.endsOn}
                onChange={(event) => {
                  setSeasonForm({ ...seasonForm, endsOn: event.target.value });
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <Button type="submit" variant="contained" disabled={!canSubmitSeason(seasonForm)}>
                {translate(locale, 'teams.season.create.submit')}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
